import AppConfig from 'config/AppConfig';
import axios, { AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { v4 as uuidv4 } from 'uuid';

interface RequestParameters {
  [key: string]: string | string[] | number | boolean;
}

export function createDevHeaders(): Record<string, string> {
  const devHeaders: Record<string, string> = {};

  if (AppConfig.userIdClaim !== undefined) {
    devHeaders['x-claim-userId'] = AppConfig.userIdClaim;
  }

  if (AppConfig.userTypeClaim !== undefined) {
    devHeaders['x-claim-userType'] = AppConfig.userTypeClaim;
  }

  if (AppConfig.agencyClaim !== undefined) {
    devHeaders['x-claim-agency'] = AppConfig.agencyClaim;
  }

  if (AppConfig.rolesClaim !== undefined) {
    devHeaders['x-claim-roles'] = AppConfig.rolesClaim;
  }

  return devHeaders;
}

export class StaffApi {
  baseUrl: string;
  authToken: string;
  headers: AxiosRequestHeaders | object | null;

  constructor(
    service: string,
    authToken: string,
    uri: string | null = null,
    headers: AxiosRequestHeaders | object | null = {}
  ) {
    const serviceUri = resolveServiceUri(service, uri);

    this.baseUrl = `${serviceUri.replace(/\/$/, '')}/`;

    this.authToken = authToken;

    this.headers = { ...createDevHeaders(), ...headers };
  }

  get(url: string, parameters: RequestParameters = {}, signal?: AbortSignal) {
    return axios.get(`${this.baseUrl}${url}`, this.createParameters(parameters, {}, signal));
  }

  post(
    url: string,
    body: unknown,
    parameters = {},
    overrideHeaders = {},
    signal?: AbortSignal,
    overrideConfig: AxiosRequestConfig = {}
  ) {
    return axios.post(`${this.baseUrl}${url}`, body, {
      ...this.createParameters(parameters, overrideHeaders, signal),
      ...overrideConfig,
    });
  }

  put(url: string, body: unknown, signal?: AbortSignal) {
    return axios.put(`${this.baseUrl}${url}`, body, this.createParameters({}, {}, signal));
  }

  patch(url: string, body: unknown, signal?: AbortSignal) {
    return axios.patch(`${this.baseUrl}${url}`, body, this.createParameters({}, {}, signal));
  }

  delete(url: string, signal?: AbortSignal) {
    return axios.delete(`${this.baseUrl}${url}`, this.createParameters({}, {}, signal));
  }

  createParameters = (parameters = {}, overrideHeaders = {}, signal?: AbortSignal) => {
    const config = {
      headers: {
        Authorization: 'Bearer ' + this.authToken,
        'x-session-id': sessionStorage.getItem(http.sessionName) ?? uuidv4(),
        ...this.headers,
        ...overrideHeaders,
      },
      params: { ...parameters },
      ...(signal && { signal }), // Only add signal if it exists
      paramsSerializer: (params: RequestParameters): string => {
        return Object.keys(params)
          .map((key) => {
            const value = params[key];
            return Array.isArray(value)
              ? value
                  .map((item) => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
                  .join('&')
              : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
          })
          .join('&');
      },
    };

    return config;
  };
}

export function resolveServiceUri(service: string, uri: string | null = null): string {
  if (uri) {
    return uri;
  }

  // ./dev up sets a local URI only for services selected to run locally.
  const normalizedService = service.toLowerCase();
  const localUri = AppConfig.localUris?.[normalizedService];

  if (localUri) {
    return localUri;
  }

  return `${AppConfig.baseApiUri}/${service}`;
}

export const http = {
  get(url: string) {
    return axios.get(url);
  },
  post(url: string, body: unknown) {
    return axios.post(url, body);
  },
  put(url: string, body: unknown) {
    return axios.put(url, body);
  },
  patch(url: string, body: unknown) {
    return axios.patch(url, body);
  },
  delete(url: string) {
    return axios.delete(url);
  },
  sessionName: 'autopilot-sessionId',
};
